import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';

import { PREVIEW_SHORTCUTS } from './preview-shortcuts';
import { altKeyName } from '../alt-key-name';

/** The Alt key's name on this computer: Option on a Mac. */
const ALT = altKeyName();

/**
 * The list of Preview keyboard shortcuts, opened with `?` (Issue #3313). It reads the same table
 * as the key handler and the menus, so it always lists the keys that work. A Material dialog traps
 * focus while open and closes with Escape.
 */
@Component({
  selector: 'app-preview-shortcuts-dialog',
  imports: [MatDialogModule, MatButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <h2 mat-dialog-title id="preview-shortcuts-title">Keyboard shortcuts</h2>
    <mat-dialog-content>
      <p class="hint">On the Preview, when you are not typing in a field.</p>
      <dl class="shortcut-list" data-testid="preview-shortcuts-list" aria-labelledby="preview-shortcuts-title">
        @for (entry of shortcuts; track entry.label) {
          <div class="shortcut-row">
            <dt>{{ entry.label }}</dt>
            <dd>
              <kbd>{{ entry.shortcut }}</kbd>
            </dd>
          </div>
        }
      </dl>
      <h3 class="section-title" id="preview-mouse-title">With the mouse</h3>
      <dl class="shortcut-list" data-testid="preview-mouse-gestures" aria-labelledby="preview-mouse-title">
        @for (entry of mouseGestures; track entry.label) {
          <div class="shortcut-row">
            <dt>{{ entry.label }}</dt>
            <dd>
              <kbd>{{ entry.gesture }}</kbd>
            </dd>
          </div>
        }
      </dl>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" mat-dialog-close data-testid="preview-shortcuts-close">Close</button>
    </mat-dialog-actions>
  `,
  styles: `
    .hint {
      margin: 0 0 12px;
      color: var(--mat-sys-on-surface-variant);
    }

    .section-title {
      margin: 16px 0 4px;
      font: var(--mat-sys-title-small);
    }

    .shortcut-list {
      margin: 0;
      min-width: 280px;
    }

    .shortcut-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 24px;
      padding: 4px 0;
    }

    dt,
    dd {
      margin: 0;
    }

    kbd {
      padding: 0 6px;
      border: 1px solid var(--mat-sys-outline-variant);
      border-radius: 4px;
      font: 500 12px/18px var(--mat-sys-body-small-font, inherit);
      color: var(--mat-sys-on-surface-variant);
      white-space: nowrap;
    }
  `,
})
export class PreviewShortcutsDialogComponent {
  readonly shortcuts = PREVIEW_SHORTCUTS;
  /**
   * Holding Alt makes the pointer the text tool (Issue #3338). A plain drag pans, so selecting
   * text has no visible affordance until Alt is held; this list is where people can find it.
   */
  readonly mouseGestures: readonly { label: string; gesture: string }[] = [
    { label: 'Zoom in, or back to fit', gesture: 'Double-click' },
    { label: 'Pan', gesture: 'Drag' },
    { label: 'Select text', gesture: `Hold ${ALT} and drag` },
    { label: 'Select a word', gesture: `${ALT} + double-click` },
  ];
}
